import axios from 'axios'
import { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useTranslation, TFunction } from 'react-i18next'
import { setProfile } from '../../redux/action/profile'
import useLoadingStore from '../../stores/useLoadingStore'
import useRelogin from '../useRelogin'
import ConfigAPI from '../../constant/ConfigAPI'
import isEmail from 'validator/lib/isEmail'
import { RootStateType } from '../../redux/reducer/rootReducer'

type ParamServerSideType = {
    pageNumber: number
    rowPerPage: number
    sortField: string
    sortType: string
    keyword: string
    groupId: string
}

export interface Form {
    username?: string
    email?: string
    isyl?: string
    confirmIsyl?: string
    firstname?: string
    lastname?: string
    userGroupId?: number
    departmentId?: number
    userEnabled?: boolean
}

export const validate = async (values: Form, t: TFunction<'translation'>, action: 'add' | 'edit') => {
    const errors: Partial<Form> = {}
    if (action === 'add') {
        if (!values.username) {
            errors.username = `${t('LABEL.TEXT_REQUIRED')}`
        }
        if (!values.isyl) {
            errors.isyl = `${t('LABEL.TEXT_REQUIRED')}`
        }
        if (!values.confirmIsyl) {
            errors.confirmIsyl = `${t('LABEL.TEXT_REQUIRED')}`
        }
        if (values.confirmIsyl && values.confirmIsyl !== values.isyl) {
            errors.confirmIsyl = `${t('VALIDATE.MESSAGES.CONFIRM_PASSWORD')}`
        }
    }
    if (!values.email) {
        errors.email = `${t('LABEL.TEXT_REQUIRED')}`
    }
    if (values.email && !isEmail(values.email)) {
        errors.email = `${t('VALIDATE.MESSAGES.EMAIL')}`
    }
    if (!values.firstname) {
        errors.firstname = `${t('LABEL.TEXT_REQUIRED')}`
    }
    if (!values.lastname) {
        errors.lastname = `${t('LABEL.TEXT_REQUIRED')}`
    }
    if (!values.userGroupId) {
        errors.userGroupId = `${t('LABEL.SELECT_REQUIRED')}` as any
    }
    if (!values.departmentId) {
        errors.departmentId = `${t('LABEL.SELECT_REQUIRED')}` as any
    }
    return errors
}

const useUser = () => {
    const valueSubmitted = useRef<Form>()
    const [departmentList, setDepartmentList] = useState<LookupType[]>([])
    const [userGroupList, setUserGroupList] = useState<LookupType[]>([])
    const [enableUser, setEnableUser] = useState<boolean>(true)
    const [openDialogOkDelete, setOpenDialogOkDelete] = useState<boolean>(false)
    const [openDialogDelete, setOpenDialogDelete] = useState<boolean>(false)
    const [openDialogBack, setOpenDialogBack] = useState<boolean>(false)
    const [openDialogSave, setOpenDialogSave] = useState<boolean>(false)
    const [openDialogOk, setOpenDialogOk] = useState<boolean>(false)
    const [alertMessageOk, setAlertMessageOk] = useState<string>('')
    const [openDialogError, setOpenDialogError] = useState<boolean>(false)
    const [alertMessageError, setAlertMessageError] = useState<string>('')
    const [statusCode, setStatusCode] = useState<number>()
    const [isDuplicate, setIsDuplicate] = useState<boolean>(false)
    const [actionType, setActionType] = useState<string>('')
    const [data, setData] = useState<any[]>([])
    const [totalItem, setTotalItem] = useState<number>(0)
    const [userId, setUserId] = useState<number>(0)
    const [paramServerSide, setParamServerSide] = useState<ParamServerSideType>({
        pageNumber: 1,
        rowPerPage: 10,
        sortField: 'username',
        sortType: 'asc',
        keyword: '',
        groupId: '',
    })
    const { reLoginForTokenExpired, callServiceReLogin } = useRelogin()
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const history = useHistory()
    const loadingStore = useLoadingStore()
    const { isLoggedIn } = useSelector(({ profile }: RootStateType) => profile)

    useEffect(() => {
        if (isLoggedIn) {
            callServiceSearch(paramServerSide)
            callServiceDetailSetup()
        }
    }, [isLoggedIn])

    const callServiceSearch = async (params: ParamServerSideType) => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/users/manage/?pageNumber=${params.pageNumber}&rowPerPage=${params.rowPerPage}&sortField=${params.sortField}&sortType=${params.sortType}&keyword=${params.keyword}&groupId=${params.groupId}`
            const result = await axios.get(url, ConfigAPI)
            if (result) {
                setData(result.data.data)
                setTotalItem(result.data.total)
                setParamServerSide({
                    pageNumber: params.pageNumber,
                    rowPerPage: params.rowPerPage,
                    sortField: params.sortField,
                    sortType: params.sortType,
                    keyword: params.keyword,
                    groupId: params.groupId,
                })
            }
        } catch (error) {
            //@ts-ignore
            const errMsg: string = error?.toString()
            if (errMsg) {
                if (errMsg.includes('401')) {
                    //reLoginForTokenExpired('/backoffice/user-management')
                    //callServiceReLogin('/backoffice/user-management')
                }
            }
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const callServiceDetailSetup = async () => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/users/manage/detailsetup`
            const result = await axios.get(url, ConfigAPI)
            if (result.data) {
                if (result.data.groupList) {
                    setUserGroupList([{ id: 0, name: 'ทั้งหมด' }, ...result.data.groupList])
                }
                if (result.data.departmentList) {
                    setDepartmentList(result.data.departmentList)
                }
            }
        } catch (error) {
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const callServiceGetById = async (id: number) => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/users/manage/${id}`
            const result = await axios.get(url, ConfigAPI)
            if (result.data) {
                setStatusCode(200)
            }
            return { result }
        } catch (error) {
            //@ts-ignore
            const errMsg: string = error?.toString()
            if (errMsg) {
                if (errMsg.includes('401')) {
                    //reLoginForTokenExpired()
                    //callServiceReLogin()
                } else {
                    setStatusCode(500)
                    setOpenDialogError(true)
                    setAlertMessageError(t('ALERT.CONTENT.NOT_RETRIEVE_DATA'))
                }
            }
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const callServiceAdd = async (data: Form) => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/users/manage`
            const reqParams = {
                username: data.username,
                email: data.email,
                password: data.isyl,
                title: '',
                firstName: data.firstname,
                lastName: data.lastname,
                groupId: data.userGroupId,
                departmentId: data.departmentId,
                isEnabled: data.userEnabled,
            }

            const result = await axios.post(url, reqParams, ConfigAPI)
            if (result.data.message === 'success') {
                setStatusCode(200)
                setAlertMessageOk(t('ALERT.CONTENT.SAVE_SUCCESS'))
            } else if (result.data.message === 'duplicated') {
                setStatusCode(200)
                setIsDuplicate(true)
                setAlertMessageOk(t('ALERT.CONTENT.SAVE_DUPLICATED'))
            } else {
                setStatusCode(200)
                setAlertMessageOk(result.data.message)
            }
            setOpenDialogOk(true)

            return { result }
        } catch (error) {
            //@ts-ignore
            const errMsg: string = error?.toString()
            if (errMsg) {
                if (errMsg.includes('401')) {
                    //reLoginForTokenExpired()
                    //callServiceReLogin()
                } else {
                    setStatusCode(500)
                    setOpenDialogError(true)
                    setAlertMessageError(t('ALERT.CONTENT.SAVE_NOT_SUCCESS'))
                }
            }
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const callServiceEdit = async (id: number, data: Form) => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/users/manage/${id}`
            const reqParams = {
                email: data.email,
                firstName: data.firstname,
                lastName: data.lastname,
                groupId: data.userGroupId,
                departmentId: data.departmentId,
                isEnabled: data.userEnabled,
            }

            const result = await axios.put(url, reqParams, ConfigAPI)
            if (result.data.message === 'success') {
                setStatusCode(200)
                setAlertMessageOk(t('ALERT.CONTENT.SAVE_SUCCESS'))
                callServiceFetchProfile()
            } else if (result.data.message === 'duplicated') {
                setStatusCode(200)
                setIsDuplicate(true)
                setAlertMessageOk(t('ALERT.CONTENT.SAVE_DUPLICATED'))
            } else {
                setStatusCode(200)
                setAlertMessageOk(result.data.message)
            }
            setOpenDialogOk(true)

            return { result }
        } catch (error) {
            //@ts-ignore
            const errMsg: string = error?.toString()
            if (errMsg) {
                if (errMsg.includes('401')) {
                    //reLoginForTokenExpired()
                    //callServiceReLogin()
                } else {
                    setStatusCode(500)
                    setOpenDialogError(true)
                    setAlertMessageError(t('ALERT.CONTENT.SAVE_NOT_SUCCESS'))
                }
            }
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const callServiceDelete = async (id: number) => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/users/manage/${id}`
            const result = await axios.delete(url, ConfigAPI)
            if (result.data.message === 'success') {
                setData(data.filter((item) => item.userId !== userId))
                setTotalItem(totalItem - 1)
                setUserId(0)
            }
            setOpenDialogOkDelete(true)

            return { result }
        } catch (error) {
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const callServiceFetchProfile = async () => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/auth/fetchProfile`
            const result = await axios.get(url, ConfigAPI)
            if (result.status === 200) {
                dispatch(setProfile(result.data))
            }
            return { result }
        } catch (error) {
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const handleChangePageNumber = (page: number) => {
        callServiceSearch({
            ...paramServerSide,
            pageNumber: page,
        })
    }

    const handleSort = (sortField: string, sortType: string) => {
        const params: ParamServerSideType = {
            ...paramServerSide,
            sortField: sortField,
            sortType: sortType,
        }
        callServiceSearch(params)
    }

    const handleChangeEnableUser = () => {
        setEnableUser(!enableUser)
    }

    const handleSubmitSearch = (values: { keyword: string; userGroupId: number }) => {
        const params: ParamServerSideType = {
            ...paramServerSide,
            pageNumber: 1,
            keyword: values?.keyword ? values.keyword.trim() : '',
            groupId: values?.userGroupId ? values.userGroupId.toString() : '',
        }
        callServiceSearch(params)
    }

    const handleSubmitForm = (values: any) => {
        valueSubmitted.current = { ...values, userEnabled: enableUser }
        setActionType('save')
        setOpenDialogSave(true)
    }

    const handleClickAdd = () => {
        history.push('/backoffice/user-management/add')
    }

    const handleClickEdit = async (data: any) => {
        const userId = data.userId
        setUserId(userId)
        history.push(`/backoffice/user-management/${userId}/edit`)
    }

    const handleClickDelete = (data: any) => {
        const userId = data.userId
        setUserId(userId)
        setOpenDialogDelete(true)
    }

    const handleClickForceBack = () => {
        history.push('/backoffice/user-management')
    }

    const handleClickBack = (dirty: boolean) => {
        // ตอนกดปุ่มยกเลิกที่ form
        if (dirty) {
            setOpenDialogBack(true)
        } else {
            setOpenDialogBack(false)
            history.push('/backoffice/user-management')
        }
        setActionType('back')
    }

    const handleClickCancelDelete = () => {
        setOpenDialogDelete(false)
    }

    const handleClickConfirmDelete = () => {
        setOpenDialogDelete(false)
        callServiceDelete(userId)
    }

    const handleClickCancelBack = () => {
        setOpenDialogBack(false)
    }

    const handleClickConfirmBack = () => {
        setOpenDialogBack(false)
        history.push('/backoffice/user-management')
    }

    const handleClickCancelSave = () => {
        setOpenDialogSave(false)
    }

    const handleClickConfirmSave = (type: 'add' | 'edit', id: number) => {
        if (type === 'add') {
            callServiceAdd(valueSubmitted.current as Form)
        }
        if (type === 'edit') {
            callServiceEdit(id, valueSubmitted.current as Form)
        }

        setOpenDialogSave(false)
    }

    const handleClickOk = () => {
        setOpenDialogOk(false)
        setOpenDialogError(false)
        if (statusCode === 401) {
            history.push('/login')
        } else if (!isDuplicate && statusCode !== 500) {
            history.push('/backoffice/user-management')
        }
        setIsDuplicate(false)
    }

    const handleClickOkDelete = () => {
        setOpenDialogOkDelete(false)
    }

    return {
        onSubmitForm: handleSubmitForm,
        onSubmitSearch: handleSubmitSearch,
        onChangePageNumber: handleChangePageNumber,
        onChangeEnableUser: handleChangeEnableUser,
        onSort: handleSort,
        onClickAdd: handleClickAdd,
        onClickEdit: handleClickEdit,
        onClickDelete: handleClickDelete,
        onClickOkDelete: handleClickOkDelete,
        onClickCancelBack: handleClickCancelBack,
        onClickCancelSave: handleClickCancelSave,
        onClickCancelDelete: handleClickCancelDelete,
        onClickConfirmBack: handleClickConfirmBack,
        onClickConfirmSave: handleClickConfirmSave,
        onClickConfirmDelete: handleClickConfirmDelete,
        onClickForceBack: handleClickForceBack,
        onClickBack: handleClickBack,
        onClickOk: handleClickOk,
        callServiceGetById,
        setEnableUser,
        departmentList,
        userGroupList,
        enableUser,
        data,
        totalItem,
        paramServerSide,
        openDialogOkDelete,
        openDialogDelete,
        openDialogBack,
        openDialogSave,
        openDialogOk,
        alertMessageOk,
        openDialogError,
        alertMessageError,
        actionType,
    }
}

export default useUser
